import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { WebViewerWrapper, PDFManagerFactory } from 'pdftron';
import { DocumentTypes, PDFTronTools, Difference, SamplingPoint, InputAnnotation, ColorPickerCustomData } from 'types';
import AnnotationMixin from 'pdftron/docManager/Annotation';
import GVAnnotationMixin from './utils/GVAnnotationMixin';
import { COLOR_POINT_ANNOTATION_SIZE, PREP_TOOL_LABEL, PREP_TOOL_ANNOTATION } from './utils/annotationConstants';
import drawMarkupLabel from './utils/drawMarkupLabel';
import { getPageRotation } from './utils/labelPositionUtils';
import { sampleHexColorFromPDF } from 'utils/colorSampling';
import { generateColorPickerCursor } from 'utils/liveColorSampling';
import store from 'store/store';
import { inspection, app, getNextColorPointId, getFocusedDifferenceId, getExcludedPage } from 'store';
import { useZoomStateStore } from 'zstore/zoomStateStore';
const COLOR_POINT_HITBOX = COLOR_POINT_ANNOTATION_SIZE;

class ColorPickerZone {
  source: WebViewerWrapper | null = null;
  target: WebViewerWrapper | null = null;

  private isCreatingAnnotation: boolean = false;

  constructor(source: WebViewerWrapper | null, target: WebViewerWrapper | null) {
    this.source = source;
    this.target = target;
  }

  get annotationMixin() {
    return new AnnotationMixin(this.source, this.target);
  }

  createColorPointAnnotation(instance: WebViewerInstance) {
    class ColorPointAnnotation extends GVAnnotationMixin(instance.Core.Annotations.RectangleAnnotation) {
      // Store the point number as a direct property for reliable access
      pointNumber: number = 0;

      constructor(pointId?: number | null) {
        super();

        this.setCustomData(ColorPickerCustomData.colorPickerAnnotation, 'true');
        this.ToolName = PDFTronTools.COLOR_PICKER;
        this.IsHoverable = true;
        this.disableRotationControl();
        this.NoResize = true;
        this.ReadOnly = false;
        this.Listable = true;

        if (pointId && pointId > 0) {
          this.pointNumber = pointId;
          this.setCustomData(ColorPickerCustomData.colorPointId, pointId.toString());
          this.Subject = `Color Point ${pointId}`;
        }

        this.StrokeColor = new instance.Core.Annotations.Color(234, 54, 50);
        this.FillColor = new instance.Core.Annotations.Color(234, 54, 50, 1);
        this.StrokeThickness = 0;
        // Use larger hitbox for easier selection, visual appearance stays small
        this.Width = COLOR_POINT_HITBOX;
        this.Height = COLOR_POINT_HITBOX;
      }

      draw(ctx: CanvasRenderingContext2D, pageMatrix: any, pageRotation: number, zoomLevel: number) {
        this.setStyles(ctx, pageMatrix);

        const centerX = parseFloat(this.getCustomData(ColorPickerCustomData.sampleX)) || this.X + this.Width / 2;
        const centerY = parseFloat(this.getCustomData(ColorPickerCustomData.sampleY)) || this.Y + this.Height / 2;

        // Draw invisible clickable area (minimum opacity 0.01 required for hit detection)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.01)';
        ctx.fillRect(this.X, this.Y, this.Width, this.Height);

        const { DEFAULT_FILL_STYLE, SELECTED_FILL_STYLE } = PREP_TOOL_ANNOTATION;
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = SELECTED_FILL_STYLE;
        ctx.lineWidth = 1;
        ctx.strokeRect(this.X, this.Y, this.Width, this.Height);

        ctx.fillStyle = SELECTED_FILL_STYLE;
        ctx.fillRect(centerX - 1, centerY - 1, 2, 2);

        const selectedAnnotations = instance.Core.annotationManager.getSelectedAnnotations();
        const isSelected = selectedAnnotations.some((targetAnnot) => targetAnnot.Id === this.Id);
        const displayNumber = this.getDisplayNumber();
        if (displayNumber && (isSelected || (this as any)?.IsHovering)) {
          const realPageRotation = getPageRotation(instance, this.PageNumber);
          const labelText = `Color Point #${displayNumber}`;
          const { HEIGHT, WIDTH_PADDING, GAP } = PREP_TOOL_LABEL;
          const fillStyle = isSelected ? SELECTED_FILL_STYLE : DEFAULT_FILL_STYLE;
          const pageWidth = instance.Core.documentViewer.getPageWidth(this.PageNumber);
          const pageHeight = instance.Core.documentViewer.getPageHeight(this.PageNumber);
          drawMarkupLabel(
            ctx,
            labelText,
            this.X,
            this.Y,
            HEIGHT,
            GAP,
            WIDTH_PADDING,
            fillStyle,
            zoomLevel,
            false,
            this.Width,
            this.Height,
            realPageRotation,
            pageWidth,
            pageHeight,
          );
        }
      }

      private getDisplayNumber(): number | null {
        if (this.pointNumber > 0) return this.pointNumber;

        const colorPointId = this.getCustomData(ColorPickerCustomData.colorPointId);
        const parsed = parseInt(colorPointId, 10);
        if (!isNaN(parsed) && parsed > 0) {
          this.pointNumber = parsed;
          return parsed;
        }

        const match = this.Subject?.match(/Color Point (\d+)/);
        if (match?.[1]) {
          const subjectParsed = parseInt(match[1], 10);
          if (!isNaN(subjectParsed) && subjectParsed > 0) {
            this.pointNumber = subjectParsed;
            this.setCustomData(ColorPickerCustomData.colorPointId, subjectParsed.toString());
            return subjectParsed;
          }
        }

        return null;
      }
    }
    return ColorPointAnnotation;
  }

  createColorPickerZoneAnnotationTools() {
    if (!this.target) return;
    const { instance } = this.target;

    const colorPickerZoneInstance = this;

    const DEFAULT_CURSOR_COLOR = '#00C8C8';

    const toolObject = new (class ColorPickerTool extends instance.Core.Tools.Tool {
      private mouseMoveHandler: ((e: Event) => void) | null = null;
      private currentCursor: string = '';
      private pendingSample: boolean = false;
      private lastSampleTime: number = 0;
      private lastMouseEvent: MouseEvent | null = null;
      private trailingUpdateScheduled: boolean = false;
      private readonly SAMPLE_INTERVAL = 50;

      constructor() {
        super(instance.Core.documentViewer);
      }

      private isPageExcluded(page: number): boolean {
        const excluded = getExcludedPage(store.getState())[DocumentTypes.target];
        return excluded.indexOf(page) > -1 && excluded.length !== 0;
      }

      private setCursorVariable(viewerElement: HTMLElement | null, value: string) {
        if (!viewerElement) return;
        if (value !== this.currentCursor) {
          this.currentCursor = value;
          viewerElement.style.setProperty('--color-picker-cursor', value);
        }
      }

      private scheduleTrailingUpdate(currentEvent: MouseEvent) {
        if (this.trailingUpdateScheduled) return;
        if (!this.lastMouseEvent || this.lastMouseEvent === currentEvent) return;

        this.trailingUpdateScheduled = true;
        requestAnimationFrame(() => {
          this.trailingUpdateScheduled = false;
          if (this.mouseMoveHandler && this.lastMouseEvent) {
            this.lastSampleTime = 0;
            this.updateCursor(this.lastMouseEvent);
          }
        });
      }

      private async updateCursor(e: MouseEvent) {
        const now = Date.now();
        if (this.pendingSample || now - this.lastSampleTime < this.SAMPLE_INTERVAL) {
          return;
        }

        const docViewer = instance.Core.documentViewer;

        const mouseLocation = this.getMouseLocation(e);
        if (!mouseLocation) return;

        const displayMode = docViewer.getDisplayModeManager().getDisplayMode();
        const page = displayMode.getSelectedPages(mouseLocation, mouseLocation).first;
        if (!page) return;

        const viewerElement = docViewer.getScrollViewElement() as HTMLElement | null;

        if (this.isPageExcluded(page)) {
          this.setCursorVariable(viewerElement, 'not-allowed');
          return;
        }

        const pageCoordinates = displayMode.windowToPage(mouseLocation, page);

        this.pendingSample = true;
        this.lastSampleTime = now;

        try {
          const hexColor = await sampleHexColorFromPDF(docViewer, page, pageCoordinates.x, pageCoordinates.y);

          const newCursor = generateColorPickerCursor(hexColor);
          this.setCursorVariable(viewerElement, newCursor);
        } catch (error) {
          const fallback = generateColorPickerCursor(DEFAULT_CURSOR_COLOR);
          this.setCursorVariable(viewerElement, fallback);
        } finally {
          this.pendingSample = false;
          this.scheduleTrailingUpdate(e);
        }
      }

      switchIn(previousTool: any) {
        super.switchIn(previousTool);

        const viewerElement = instance.Core.documentViewer.getScrollViewElement() as HTMLElement | null;
        viewerElement?.classList.add('manual-tool-mode', 'color-picker-cursor');

        if (viewerElement) {
          const defaultCursor = generateColorPickerCursor(DEFAULT_CURSOR_COLOR);
          viewerElement.style.setProperty('--color-picker-cursor', defaultCursor);
        }

        this.mouseMoveHandler = (e: Event) => {
          this.lastMouseEvent = e as MouseEvent;
          this.updateCursor(e as MouseEvent);
        };

        viewerElement?.addEventListener('mousemove', this.mouseMoveHandler);
      }

      switchOut(newTool: any) {
        super.switchOut(newTool);

        const viewerElement = instance.Core.documentViewer.getScrollViewElement() as HTMLElement | null;
        viewerElement?.classList.remove('manual-tool-mode', 'color-picker-cursor');

        if (this.mouseMoveHandler && viewerElement) {
          viewerElement.removeEventListener('mousemove', this.mouseMoveHandler);
          this.mouseMoveHandler = null;
        }

        if (viewerElement) {
          viewerElement.style.removeProperty('--color-picker-cursor');
        }

        this.currentCursor = '';
        this.lastMouseEvent = null;
        this.trailingUpdateScheduled = false;
      }

      async mouseLeftDown(e: any) {
        if (colorPickerZoneInstance.isCreatingAnnotation) return false;

        colorPickerZoneInstance.dispatchStoreAction((inspection) => inspection.actions.unfocusDifference());
        instance.Core.annotationManager.deselectAllAnnotations();
        if (colorPickerZoneInstance.source) {
          colorPickerZoneInstance.source.instance.Core.annotationManager.deselectAllAnnotations();
        }

        const mouseLocation = this.getMouseLocation(e);
        if (!mouseLocation) return false;

        const displayMode = instance.Core.documentViewer.getDisplayModeManager().getDisplayMode();
        const page = displayMode.getSelectedPages(mouseLocation, mouseLocation).first;
        if (!page) return false;

        if (this.isPageExcluded(page)) {
          store.dispatch(app.actions.setSnackMessage({ message: 'This page has been excluded', type: 'error' }));
          return false;
        }

        const pageCoordinates = displayMode.windowToPage(mouseLocation, page);
        colorPickerZoneInstance.isCreatingAnnotation = true;

        try {
          const uniqueId = colorPickerZoneInstance.getNextUniqueId();
          const allPoints = colorPickerZoneInstance.getAllColorPointAnnotations();

          const usedNumbers = new Set(allPoints.map((point) => point.id));

          const visualNumber = colorPickerZoneInstance.getNextAvailableNumber(usedNumbers);

          const ColorPointAnnotation = colorPickerZoneInstance.createColorPointAnnotation(instance);
          const colorPointAnnotation = new ColorPointAnnotation(visualNumber);

          const halfHitbox = COLOR_POINT_HITBOX / 2;
          colorPointAnnotation.X = pageCoordinates.x - halfHitbox;
          colorPointAnnotation.Y = pageCoordinates.y - halfHitbox;
          colorPointAnnotation.PageNumber = page;
          colorPointAnnotation.setCustomData('uniqueId', uniqueId.toString());
          colorPointAnnotation.setCustomData(ColorPickerCustomData.sampleX, pageCoordinates.x.toString());
          colorPointAnnotation.setCustomData(ColorPickerCustomData.sampleY, pageCoordinates.y.toString());

          instance.Core.annotationManager.addAnnotation(colorPointAnnotation, { imported: false });

          setTimeout(() => {
            instance.Core.annotationManager.selectAnnotation(colorPointAnnotation);
            colorPickerZoneInstance.showDeletePopups();
          }, 0);

          colorPickerZoneInstance.annotationMixin.updateInputAnnotations();
        } finally {
          colorPickerZoneInstance.isCreatingAnnotation = false;
        }

        e.preventDefault();
        return false;
      }
    })();

    this.target.registerTool({
      toolName: PDFTronTools.COLOR_PICKER,
      toolObject,
      buttonImage: '',
    });
  }

  getNextUniqueId(): number {
    const currentId = getNextColorPointId(store.getState());
    store.dispatch(inspection.actions.increaseColorPointId());
    return currentId;
  }

  resetUniqueIdCounter(): void {
    store.dispatch(inspection.actions.setColorPointId(1));
  }

  private isColorPointAnnotation(annot: Core.Annotations.Annotation): boolean {
    return annot.getCustomData(ColorPickerCustomData.colorPickerAnnotation) === 'true';
  }

  // find the next available color point number
  private getNextAvailableNumber(usedNumbers: Set<number>): number {
    let number = 1;
    while (usedNumbers.has(number)) {
      number++;
    }
    return number;
  }

  clearAllColorPoints() {
    if (this.target?.annotationManager) {
      const colorPointAnnotations = this.target.annotationManager
        .getAnnotationsList()
        .filter((annot) => this.isColorPointAnnotation(annot));

      if (colorPointAnnotations.length > 0) {
        this.target.annotationManager.deleteAnnotations(colorPointAnnotations, { imported: false });
      }
    }

    this.resetUniqueIdCounter();
    this.annotationMixin.updateInputAnnotations();
  }

  getAllColorPointAnnotations(): Array<{
    id: number;
    annotation: Core.Annotations.Annotation;
  }> {
    if (!this.target?.annotationManager) return [];

    const colorPoints = [];
    const targetAnnotations = this.target.annotationManager.getAnnotationsList();

    for (const annotation of targetAnnotations) {
      if (!this.isColorPointAnnotation(annotation)) continue;

      const pointId = parseInt(annotation.getCustomData(ColorPickerCustomData.colorPointId), 10);
      if (!isNaN(pointId)) {
        colorPoints.push({ id: pointId, annotation });
      }
    }

    return colorPoints.sort((a, b) => a.id - b.id);
  }

  getSerializedSamplingPoints(): SamplingPoint[] {
    const colorPoints = this.getAllColorPointAnnotations();

    return colorPoints.map(({ annotation }) => ({
      pageNumber: annotation.PageNumber,
      point: [
        parseFloat(annotation.getCustomData(ColorPickerCustomData.sampleX)),
        parseFloat(annotation.getCustomData(ColorPickerCustomData.sampleY)),
      ] as [number, number],
    }));
  }

  reloadSamplingPoints(annotations: InputAnnotation[]): void {
    if (!this.target) return;
    const instance = this.target.instance;
    const annotManager = instance.Core.annotationManager;

    const usedNumbers = new Set(this.getAllColorPointAnnotations().map((point) => point.id));

    // Phase 1: Create all annotations synchronously so getSerializedSamplingPoints()
    // always sees the complete set even if an autosave fires mid-reload.
    const created: Array<{ annotation: Core.Annotations.Annotation; pageNumber: number; x: number; y: number }> = [];

    for (let index = 0; index < annotations.length; index++) {
      const annot = annotations[index];
      if (!annot.samplingPoint) continue;

      const existingAnnotation = annotManager.getAnnotationById(annot.annotationId);
      if (existingAnnotation) continue;

      const [x, y] = annot.samplingPoint.point;
      const pageNumber = annot.samplingPoint.pageNumber;
      const pointNumber = this.getNextAvailableNumber(usedNumbers);
      usedNumbers.add(pointNumber);

      const ColorPointAnnotation = this.createColorPointAnnotation(instance);
      const colorPointAnnotation = new ColorPointAnnotation(pointNumber);

      colorPointAnnotation.Id = annot.annotationId;
      const halfHitbox = COLOR_POINT_HITBOX / 2;
      colorPointAnnotation.X = x - halfHitbox;
      colorPointAnnotation.Y = y - halfHitbox;
      colorPointAnnotation.PageNumber = pageNumber;
      colorPointAnnotation.setCustomData('uniqueId', this.getNextUniqueId().toString());
      colorPointAnnotation.setCustomData(ColorPickerCustomData.sampleX, x.toString());
      colorPointAnnotation.setCustomData(ColorPickerCustomData.sampleY, y.toString());

      annotManager.addAnnotation(colorPointAnnotation, { imported: false });
    }
  }

  private updateAnnotationNumber(annotation: Core.Annotations.Annotation, newNumber: number): void {
    (annotation as any).pointNumber = newNumber;
    annotation.setCustomData(ColorPickerCustomData.colorPointId, newNumber.toString());
    annotation.Subject = `Color Point ${newNumber}`;
  }

  renumberColorPoints(): void {
    const colorPoints = this.getAllColorPointAnnotations();

    colorPoints.forEach((point, index) => {
      const newNumber = index + 1;
      this.updateAnnotationNumber(point.annotation, newNumber);
      this.target?.annotationManager.redrawAnnotation(point.annotation);
    });
  }

  deleteColorPoint(annotation: Core.Annotations.Annotation, documentType: DocumentTypes): void {
    const currentWrapper = this.getWrapper(documentType);
    currentWrapper?.annotationManager.deleteAnnotation(annotation, { force: true });

    this.renumberColorPoints();
    this.annotationMixin.updateInputAnnotations();
  }

  getDeleteColorPointPopup(instance: WebViewerInstance) {
    return {
      type: 'actionButton',
      img: '/icons/zoneDelete.svg',
      title: 'Delete',
      onClick: () => {
        const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];
        const documentType = instance === this.source?.instance ? DocumentTypes.source : DocumentTypes.target;
        this.deleteColorPoint(selectedAnnotation, documentType);
      },
    };
  }

  private getWrapper(documentType: DocumentTypes): WebViewerWrapper | null {
    return documentType === DocumentTypes.source ? this.source : this.target;
  }

  private dispatchStoreAction(actionCreator: (inspection: any) => any): void {
    store.dispatch(actionCreator(inspection));
  }

  colorPointAnnotationSelected(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, _action: string) {
    const focusedDifferenceId = getFocusedDifferenceId(store.getState());
    if (focusedDifferenceId && focusedDifferenceId !== '-1') {
      const currentWrapper = this.getWrapper(documentType);
      currentWrapper?.annotationManager.deselectAnnotation(annotation);
      return;
    }

    this.showDeletePopups();
  }

  private showDeletePopups() {
    if (!this.target?.instance) return;

    this.target.instance.UI.annotationPopup.update([this.getDeleteColorPointPopup(this.target.instance)]);
  }

  private updateCoordinatesAfterDrag(annotation: Core.Annotations.Annotation, documentType: DocumentTypes) {
    const currentWrapper = this.getWrapper(documentType);
    if (!currentWrapper?.instance) return;

    const centerX = annotation.X + annotation.Width / 2;
    const centerY = annotation.Y + annotation.Height / 2;
    annotation.setCustomData(ColorPickerCustomData.sampleX, centerX.toString());
    annotation.setCustomData(ColorPickerCustomData.sampleY, centerY.toString());

    currentWrapper.annotationManager.redrawAnnotation(annotation);
  }

  colorPointAnnotationChanged(documentType: DocumentTypes, annotation: Core.Annotations.Annotation, action: string) {
    if (action !== 'modify') return;

    this.updateCoordinatesAfterDrag(annotation, documentType);
    this.annotationMixin.updateInputAnnotations();
  }

  colorDifferenceFocused(differences: Difference[]) {
    const pdfDocManager = PDFManagerFactory.getPDFDocManager();

    if (!pdfDocManager || !this.source || !this.target || differences.length === 0) return;

    const difference = differences[0];

    // Zoom and scroll to the selected color difference
    pdfDocManager.zoomToDifference(difference);
    pdfDocManager.scrollToDifferences(difference);
    useZoomStateStore.getState().setZoomChanging();
  }
}

export default ColorPickerZone;
